import { firstValueFrom } from 'rxjs';
import {
  Component, ComponentFactoryResolver, Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DeliveryCalendar } from '@cakeiteasy/web-ui';
import moment from 'moment';
import {
  AddressLocationManager,
  CieUtilsModalManagerService, dateHelper, IDeliveryOrder, IDropdownItem,
  IGeoLocation,
  IThirdPartyOutlet, ngHelper,
  PickupCalendarDay, UiContextService,
} from '@cakeiteasy/web-utils';
import { DeliveryUiHelperService } from '../../services/delivery-ui-helper.service';
import { DeliveryAvailabilityService } from '../../services/delivery-availability.service';
import {
  CART_DELIVERY_TYPES,
  DELIVERY_ORDER_DELIVERY_TYPES,
} from '../../../cart';
import {
  isGeoLocationValid,
  getDeliveryFormGroup,
  getFilledDeliveryFormLocation,
  mapGeoLocationToThirdPartyLocation, receiveWoltErrorTransKeys
} from '../../delivery-helper';

import { LookupAddressDeliveryBase } from '../../../base-classes/lookup-address-delivery.base';
import { DeliveryApiService } from '../../../api-services/services/delivery-api.service';
import { DeliveryFeePayload, IThirdPartyDelivery } from '../../../delivery/models/third-party.interface';
import { MAX_ADVANCE_DAYS_FOR_THIRD_PARTY_DELIVERY } from '../../../constants';
import { TranslateService } from '@ngx-translate/core';
import { IDeliveryAddress } from '../../../users';

@Component({
  standalone: false,
  selector: 'third-party-delivery',
  styleUrls: ['./third-party-delivery.component.scss'],
  templateUrl: './third-party-delivery.component.html',
})
export class ThirdPartyDeliveryComponent extends LookupAddressDeliveryBase implements OnInit, OnChanges {
  @Input() isAsapAvailable: boolean;
  calendar: DeliveryCalendar = null;
  isCalendarLoading = false;
  skeletonPromise: Promise<any> = null;

  geoLocation: IGeoLocation;
  pickupOutlet: IThirdPartyOutlet = null;
  deliveryPromiseInfo: { externalDeliveryPromiseId: string, externalDeliveryPromiseValidUntil: string } = null;
  payloadBuffer: any = {};

  asapPickupOutlet: IThirdPartyOutlet = null;
  asapDeliveryPromiseInfo: { externalDeliveryPromiseId: string, externalDeliveryPromiseValidUntil: string } = null;
  asapTime: string | null = null;
  asapPrice: number | null = null;
  asapFlowActivated = false;


  constructor(protected addressManager: AddressLocationManager,
              protected deliveryUi: DeliveryUiHelperService,
              protected modalManager: CieUtilsModalManagerService,
              protected componentResolver: ComponentFactoryResolver,
              private deliveryAvailability: DeliveryAvailabilityService,
              private deliveryApi: DeliveryApiService,
              private translationService: TranslateService,
              private _uiContext: UiContextService,
              private formBuilder: FormBuilder) {
    super(addressManager, modalManager, componentResolver);

    this.deliveryForm = this.formBuilder.group(getDeliveryFormGroup());
  }

  ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'deliveryAddresses', (deliveryAddresses: IDeliveryAddress[]) => {
      this.initByFavoriteAddress(deliveryAddresses);
    });

    ngHelper.ifChanged(changes, 'isAsapAvailable', () => {
      if (this.geoLocation) {
        this.initAsapAvailability();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.deliveryNote = this.getDeliveryNote();
    this.maxDate = dateHelper.addDaysToDate(new Date(), (MAX_ADVANCE_DAYS_FOR_THIRD_PARTY_DELIVERY - 1));

    const initialValue = this.draft || this.getConfirmedPatchData();
    if (initialValue) {
      this.patchDeliveryForm(initialValue);

      if (initialValue.deliveryAddress) {
        await this.initLookupAddress(initialValue);
      }
    }

    this._subscribeOnDeliveryDate();
    this._watchDraft();
  }

  isNextBtnDisabled() {
    return super.isNextBtnDisabled()
      || (this.asapFlowActivated ? !this.asapPickupOutlet : !this.pickupOutlet);
  }

  mapScheduleToTimeItems(schedule: string[]): IDropdownItem[] {
    return schedule.map((timeRange: string) => ({ id: timeRange, label: timeRange, ref: { timeRange } }));
  }

  findSchedule(schedule: string[], timeValue: string): string {
    return schedule.find((item) => item === timeValue);
  }

  calendarFetcher(monthDate: Date, prevCalendar?: DeliveryCalendar) {
    return this.getCalendar({ monthsCount: 2, monthDate, prevCalendar });
  }

  getCalendar({ monthDate, prevCalendar, monthsCount = 1 }: {
    monthDate: Date,
    prevCalendar?: DeliveryCalendar,
    monthsCount?: number
  }): Promise<DeliveryCalendar> {
    return this.deliveryAvailability.getExternalDeliveryCalendar({
      fromDate: monthDate,
      prevCalendar,
      monthsCount,
      params: this.getParamsFromBuffer()
    });
  }

  getDeliveryNote(): string {
    const note = this.translationService.instant('orderProcess.delivery.deliveryForms.dateInAdvanceNote');
    return `&nbsp;<span>${note}</span>`;
  }

  setPrice(price: number) {
    this.price = price;
    this.isPriceSet = true;
  }

  resetDeliveryState() {
    super.resetDeliveryState();
    this.geoLocation = null;
    this.pickupOutlet = null;
    this.deliveryPromiseInfo = null;
    this.calendar = null;
  }

  async onAddressSelected(address: string) {
    this.skeletonPromise = (async () => {
      this.resetDeliveryState();

      this.geoLocation = await (this.busy = firstValueFrom(this.addressManager.getLocationInfo(address, this.countryCode)));

      if (isGeoLocationValid(this.geoLocation)) {
        await this.resolveDeliveryFeeForGeoLocation(this.geoLocation);
        await this.initAsapAvailability();

        const calendarRequestDate = this._getCalendarRequestDate();
        await this.loadCalendar(calendarRequestDate);
        await this._handleDeliveryDateChange(calendarRequestDate);
      } else {
        const { address: { streetNumber } } = this.geoLocation;
        this.geoLocationStatus = streetNumber ? this.STATUS.INVALID : this.STATUS.MISSING_STREET_NUMBER;
      }
    })();
  }

  private _getCalendarRequestDate(): string {
    return this.deliveryForm.get('deliveryDate').value || moment().format('YYYY-MM-DD');
  }

  confirmDelivery() {
    const { DELIVERY: type } = CART_DELIVERY_TYPES;
    const { THIRD_PARTY: deliveryType } = DELIVERY_ORDER_DELIVERY_TYPES;

    this.onNext.emit({
      deliveryOrder: {
        ...this.getDeliveryOrderForConfirm(),
        ...this.deliveryForm.getRawValue(),
        ...this.getDataAccordingAsap(),
        deliveryAgent: null,
        deliveryType, type
      },
      saveCurrentDelivery: this.saveAddressOnSubmit
    });
  }

  private getDataAccordingAsap() {
    return this.asapFlowActivated
      ? { outlet: this.asapPickupOutlet.id, externalDeliveryFee: this.asapPrice, ...this.asapDeliveryPromiseInfo }
      : { outlet: this.pickupOutlet.id, externalDeliveryFee: this.price, ...this.deliveryPromiseInfo };
  }

  async receiveScheduleFromDay(day: PickupCalendarDay): Promise<string[]> {
    const scheduledDropoffDate = this.deliveryForm.get('deliveryDate').value;
    const { schedule } = await (this.busy = this.getAvailableTime(scheduledDropoffDate));
    return schedule;
  }

  async scheduleChange(calendarDay: PickupCalendarDay) {
    this.resetError();

    const schedule = await this.changeScheduleFlow(calendarDay);

    if (!schedule) {
      return;
    }

    const selectedSchedule = this.findSchedule(schedule as string[], this.getTimeValue());

    if (selectedSchedule) {
      await (this.busy = this.checkDeliveryFeeOnDateTimeChange(calendarDay.date, this.getTimeValue()));
    } else {
      this.deliveryForm.get('deliveryTime').patchValue(null);
      setTimeout(() => this.rangeDropDown.open(), 100);
    }
  }

  async onMonthNavigate(monthDate: string) {
    await this.loadCalendar(monthDate, this.calendar);
  }

  contextInit(context) {
    this.dp = context;

    this._tryToDatepickerScroll();
  }

  timeChanged(event: IDropdownItem) {
    this.resetError();

    const dateValue = this.deliveryForm.get('deliveryDate').value;
    if (dateValue) {
      this.busy = this.checkDeliveryFeeOnDateTimeChange(dateValue, event.id as string);
    }
  }

  selectAsapTime() {
    this.deliveryForm.patchValue({
      deliveryDate: dateHelper.convertDateToStr(new Date()),
      deliveryTime: this.asapTime
    });

    this.asapFlowActivated = true;
  }

  pickLatterTime() {
    this.asapFlowActivated = false;
  }

  private async initAsapAvailability() {
    const scheduledDropoffDate = dateHelper.convertDateToStr(new Date());

    const { schedule, id: outletId } = this.isAsapAvailable
      ? await this.getAvailableTime(scheduledDropoffDate)
      : { schedule: null, id: null };

    if (schedule && schedule.length > 0) {
      this.asapTime = schedule[0];

      const scheduledDropoffTime = this.asapTime;

      const {
        pickup: { outlet }, fee: { amount }, externalDeliveryPromiseId, externalDeliveryPromiseValidUntil
      } = await this.getDeliveryFeeByDateTime({ scheduledDropoffDate, scheduledDropoffTime, outlet: outletId });

      this.asapPrice = amount;
      this.asapPickupOutlet = outlet;
      this.asapDeliveryPromiseInfo = { externalDeliveryPromiseId, externalDeliveryPromiseValidUntil };
    } else {
      this.resetAsap();
    }
  }

  private async loadCalendar(monthDate: string, prevCalendar?: DeliveryCalendar): Promise<void> {
    const fromDate = moment(monthDate, 'YYYY-MM-DD', true).toDate();
    this.isCalendarLoading = true;

    try {
      this.calendar = await this.calendarFetcher(fromDate, prevCalendar);
    } finally {
      this.isCalendarLoading = false;
    }
  }

  private _subscribeOnDeliveryDate() {
    this.addSubscriber(this.deliveryForm.get('deliveryDate').valueChanges
      .subscribe((dateValue) => this._handleDeliveryDateChange(dateValue)));
  }

  private async _handleDeliveryDateChange(dateValue: string) {
    if (!dateValue || !this.calendar || this.calendar.isDateInactive(dateValue)) {
      return;
    }

    const calendarDay = this.calendar.getDate(dateValue);
    if (calendarDay) {
      await this.scheduleChange(calendarDay as PickupCalendarDay);
    }
  }

  private resetAsap() {
    this.asapTime = null;
    this.asapPrice = null;
    this.asapPickupOutlet = null;
    this.asapDeliveryPromiseInfo = null;
  }

  private getDeliveryOrderForConfirm() {
    if (!this.deliveryOrder) {
      return null;
    }

    const { price, priceWithoutVat, ...deliveryOrder } = this.deliveryOrder;
    return deliveryOrder;
  }

  private getConfirmedPatchData(): Partial<IDeliveryOrder> | null {
    if (!this.deliveryOrder || this.deliveryOrder.type !== CART_DELIVERY_TYPES.DELIVERY) {
      return null;
    }

    const { THIRD_PARTY } = DELIVERY_ORDER_DELIVERY_TYPES;
    const { deliveryTime, deliveryType } = this.deliveryOrder;

    return {
      ...this.deliveryOrder,
      deliveryTime: deliveryType === THIRD_PARTY ? deliveryTime : null,
    };
  }

  private async getAvailableTime(scheduledDropoffDate: string): Promise<{ id: number, schedule: string[] }> {
    try {
      const dropoffLocation = mapGeoLocationToThirdPartyLocation(this.geoLocation);
      const { outletId: outlet } = this.getParamsFromBuffer();
      const payload = { scheduledDropoffDate, dropoffLocation, outlet };

      return firstValueFrom(this.deliveryApi.getExternalDeliveryAvailableTime(payload));
    } catch (e) {
      this.woltErrorHandler(e);
      return { schedule: null, id: null };
    }
  }

  private woltErrorHandler(e: any): { title?: string, message: string } {
    const { detail } = e.error;
    if (!detail || !detail.errorCode) {
      return { message: 'Unexpected error' };
    }

    this.deliveryError = this.getErrorTranslations(detail.errorCode);
  }

  private getErrorTranslations(errorCode: string): { title: string, message: string } {
    const { titleKey, messageKey } = receiveWoltErrorTransKeys(errorCode);
    const title = this.translationService.instant(titleKey);
    const message = this.translationService.instant(messageKey);

    return {
      title: title.indexOf(errorCode) === -1 ? title : '',
      message
    };
  }

  private async checkDeliveryFeeOnDateTimeChange(scheduledDropoffDate: string, scheduledDropoffTime: string): Promise<void> {
    try {
      const {
        pickup: { outlet },
        fee: { amount },
        externalDeliveryPromiseId,
        externalDeliveryPromiseValidUntil
      } = await this.getDeliveryFeeByDateTime({
        scheduledDropoffDate,
        scheduledDropoffTime,
        outlet: this.getParamsFromBuffer().outletId
      });

      this.pickupOutlet = outlet;
      this.deliveryPromiseInfo = { externalDeliveryPromiseId, externalDeliveryPromiseValidUntil };
      this.setPrice(amount);
    } catch (e) {
      this.woltErrorHandler(e);
      this.deliveryForm.get('deliveryTime').patchValue(null);
    }
  }

  private async resolveDeliveryFeeForGeoLocation(geoLocation: IGeoLocation): Promise<void> {
    try {
      const payload = { dropoffLocation: mapGeoLocationToThirdPartyLocation(geoLocation) };
      const feeResult = await this.getDeliveryFee(payload);

      this.applyFeeResult(feeResult, geoLocation);
    } catch (e) {
      this.woltErrorHandler(e);
    }
  }

  private applyFeeResult(feeResult: IThirdPartyDelivery, geoLocation: IGeoLocation) {
    this.payloadBuffer = { ...this.payloadBuffer, ...feeResult };

    this.deliveryForm.patchValue(getFilledDeliveryFormLocation(geoLocation));
    this.geoLocationStatus = this.STATUS.VALID;
  }

  private getParamsFromBuffer(): { outletId?: number } {
    const { pickup } = this.payloadBuffer as IThirdPartyDelivery;
    const outletId = pickup && pickup.outlet && pickup.outlet.id || null;

    return { outletId };
  }

  private getDeliveryFeeByDateTime({
                                     scheduledDropoffDate,
                                     scheduledDropoffTime,
                                     outlet
                                   }: {
    scheduledDropoffDate: string,
    scheduledDropoffTime: string,
    outlet: number
  }): Promise<IThirdPartyDelivery> {
    const payload = {
      outlet,
      dropoffLocation: mapGeoLocationToThirdPartyLocation(this.geoLocation),
      scheduledDropoffDate,
      scheduledDropoffTime,
    };

    return this.getDeliveryFee(payload);
  }

  private getDeliveryFee(payload: DeliveryFeePayload): Promise<IThirdPartyDelivery> {
    return firstValueFrom(this.deliveryApi.getExternalDeliveryFee(payload));
  }

  private _tryToDatepickerScroll() {
    setTimeout(() => {
      try {
        if (this.deliveryForm.get('deliveryDate').valid || !this.dp || !this.datePickerLabel
          || !this.datePickerLabel.nativeElement || this._uiContext.isMobile() || this.asapTime) {
          return;
        }

        this.dp.open();
        this.deliveryUi.scrollToDatepicker(this.datePickerLabel, 410);
      } catch (e) {
        console.error(e);
      }
    }, 700);
  }
}
